// Depth retouching brush. Edits live in a delta layer on top of the processed
// depth, so they survive changes to processing parameters.

import type { DepthData } from '../types';

export type BrushMode = 'raise' | 'lower' | 'smooth' | 'flatten';

export type RegionOp =
  | { kind: 'flat'; level?: number }
  | { kind: 'tilt'; amount: number; angle: number }
  | { kind: 'offset'; amount: number };

export interface BrushSettings {
  mode: BrushMode;
  /** Radius in output pixels. */
  radius: number;
  /** 0..1 per dab. */
  strength: number;
}

const MAX_UNDO = 20;

export class DepthEdits {
  delta: Float32Array;
  private undoStack: Float32Array[] = [];
  private flattenLevel = 0;

  constructor(
    public width: number,
    public height: number,
  ) {
    this.delta = new Float32Array(width * height);
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get isEmpty(): boolean {
    return !this.undoStack.length && this.delta.every((v) => v === 0);
  }

  /** Starts a stroke at (x, y): snapshot for undo, remember the flatten level. */
  begin(base: DepthData, x: number, y: number) {
    this.undoStack.push(this.delta.slice());
    if (this.undoStack.length > MAX_UNDO) this.undoStack.shift();
    this.flattenLevel = this.value(base, Math.round(x), Math.round(y));
  }

  undo() {
    const prev = this.undoStack.pop();
    if (prev) this.delta = prev;
  }

  clear() {
    this.undoStack.push(this.delta.slice());
    this.delta = new Float32Array(this.width * this.height);
  }

  private value(base: DepthData, x: number, y: number): number {
    const xi = Math.min(this.width - 1, Math.max(0, x));
    const yi = Math.min(this.height - 1, Math.max(0, y));
    const i = yi * this.width + xi;
    return base.data[i] + this.delta[i];
  }

  /** Applies one dab centred at (cx, cy). Returns the touched bounding box. */
  dab(base: DepthData, cx: number, cy: number, b: BrushSettings): [number, number, number, number] {
    const r = Math.max(1, b.radius);
    const x0 = Math.max(0, Math.floor(cx - r));
    const x1 = Math.min(this.width - 1, Math.ceil(cx + r));
    const y0 = Math.max(0, Math.floor(cy - r));
    const y1 = Math.min(this.height - 1, Math.ceil(cy + r));
    const k = Math.max(1, Math.round(r / 4));
    const step = b.strength * 0.04;
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const d = Math.hypot(x - cx, y - cy) / r;
        if (d >= 1) continue;
        // Smooth falloff (cosine bell).
        const w = 0.5 + 0.5 * Math.cos(Math.PI * d);
        const i = y * this.width + x;
        const cur = base.data[i] + this.delta[i];
        let target: number;
        switch (b.mode) {
          case 'raise':
            this.delta[i] += step * w;
            continue;
          case 'lower':
            this.delta[i] -= step * w;
            continue;
          case 'flatten':
            target = this.flattenLevel;
            break;
          case 'smooth': {
            // Sparse 8-neighbour average at distance k approximates a blur.
            let acc = 0;
            for (const [dx, dy] of NEIGHBOURS) acc += this.value(base, x + dx * k, y + dy * k);
            target = acc / NEIGHBOURS.length;
            break;
          }
        }
        this.delta[i] += (target - cur) * Math.min(1, b.strength * 0.5) * w;
      }
    }
    return [x0, y0, x1, y1];
  }

  /**
   * Region edit over a mask (255 = inside): flatten to a level, tilt into a
   * plane, or offset. One undo step.
   */
  region(base: DepthData, mask: Uint8Array, op: RegionOp) {
    this.undoStack.push(this.delta.slice());
    if (this.undoStack.length > MAX_UNDO) this.undoStack.shift();
    const { width: w, height: h } = this;
    // Median level and extent of the region.
    const vals: number[] = [];
    let x0 = w,
      x1 = 0,
      y0 = h,
      y1 = 0;
    for (let y = 0; y < h; y++)
      for (let x = 0; x < w; x++) {
        const i = y * w + x;
        if (!mask[i]) continue;
        if ((i & 7) === 0) vals.push(base.data[i] + this.delta[i]);
        x0 = Math.min(x0, x);
        x1 = Math.max(x1, x);
        y0 = Math.min(y0, y);
        y1 = Math.max(y1, y);
      }
    if (x1 < x0) return;
    vals.sort((a, b) => a - b);
    const median = vals[vals.length >> 1] ?? 0.5;
    const cx = (x0 + x1) / 2;
    const cy = (y0 + y1) / 2;
    const ext = Math.max(1, x1 - x0, y1 - y0) / 2;
    const rad = ((op.kind === 'tilt' ? op.angle : 0) * Math.PI) / 180;
    const [dx, dy] = [Math.cos(rad), Math.sin(rad)];
    for (let y = y0; y <= y1; y++)
      for (let x = x0; x <= x1; x++) {
        const i = y * w + x;
        if (!mask[i]) continue;
        const cur = base.data[i] + this.delta[i];
        let target: number;
        switch (op.kind) {
          case 'flat':
            target = op.level ?? median;
            break;
          case 'tilt':
            target = median + (op.amount * ((x - cx) * dx + (y - cy) * dy)) / ext;
            break;
          case 'offset':
            target = cur + op.amount;
            break;
        }
        this.delta[i] += Math.min(1, Math.max(0, target)) - cur;
      }
  }

  apply(base: DepthData): DepthData {
    const out = new Float32Array(base.data.length);
    for (let i = 0; i < out.length; i++) out[i] = Math.min(1, Math.max(0, base.data[i] + this.delta[i]));
    return { width: base.width, height: base.height, data: out };
  }
}

const NEIGHBOURS: [number, number][] = [
  [-1, -1],
  [0, -1],
  [1, -1],
  [-1, 0],
  [1, 0],
  [-1, 1],
  [0, 1],
  [1, 1],
];
